import { values } from 'lodash';

import {
  calcDiscountAmount,
  calcDiscountedPrice,
  cryptoHelper,
  IProductType, IProductTypeDiscount,
  IProductTypeListMode,
  IProductTypeOverview,
  IUpsellProductType, PRODUCT_TYPE_STRUCTURE_TYPES
} from '@cakeiteasy/web-utils';

import { ICakeCartLine } from '../models/cart/i-cake-cart-line';
import { ADDONS_TYPES, CART_LINE_TYPES, EXTRA_PRODUCT_GROUP_TYPES } from '../constants/common';
import { canHaveOrderLineBuilderId } from './order-line-builder-id-helper';

export function makeCartLinesFromAddons({ cartLine, bakeryId }: { cartLine: ICakeCartLine, bakeryId: number }): ICakeCartLine[] {
  const { imagesForPrinting, questionsMap, extraProductsMap } = cartLine;

  const lines = [];
  const applyOnce = true;
  const baseLine = {
    id: null,
    product: null,
    count: 1,
    orderLines: [],
    isCustom: true,
    bakeryId,
  };

  if (imagesForPrinting) {
    const images = imagesForPrinting
      .map(({ price, questionText, orderLineName, url }) => {
        const name = orderLineName || questionText;

        return {
          ...baseLine, name, price,
          clientId: cryptoHelper.cartClientId(),
          details: { name, url, applyOnce, addonType: ADDONS_TYPES.PRINTING_PICTURE }
        };
      });

    lines.push(...images);
  }

  if (questionsMap) {
    const questions = Object.keys(questionsMap)
      .map((questionId) => {
        const { price, addonType, questionText, orderLineName, answer: description } = questionsMap[questionId];
        const name = orderLineName || questionText;
        return {
          ...baseLine, name, price,
          clientId: cryptoHelper.cartClientId(),
          details: { name, description, applyOnce, addonType }
        };
      }, {});

    lines.push(...questions);
  }

  if (extraProductsMap && cartLine.size) {
    const { size: { extraGroups } } = cartLine;
    const extraProducts = Object.keys(extraProductsMap)
      .reduce((acc, groupId) => {
        acc.push(...extraProductsMap[groupId].map(({ description, name, picture, price }) => {
          const { name: groupName, orderLineName: groupOrderLineName, type } = extraGroups
            .find(({ id }) => id === Number(groupId));
          const actualName = type === EXTRA_PRODUCT_GROUP_TYPES.YES_NO
            ? (groupOrderLineName || groupName) : name;

          return {
            ...baseLine,
            price,
            name: actualName,
            clientId: cryptoHelper.cartClientId(),
            details: {
              name: actualName, description, applyOnce, addonType: ADDONS_TYPES.EXTRA_PRODUCT,
              ...(picture && { picture })
            }
          };
        }));
        return acc;
      }, []);
    lines.push(...extraProducts);
  }

  return lines;
}

function getProductTypeDiscountValue(
  productType: { discount?: { value?: number } }
): number {
  return (productType.discount && productType.discount.value) || 0;
}

function getStandardVariantValues(name: string) {
  const isStandardVariant = name.toLowerCase().includes('standard');
  const variantName = isStandardVariant ? '' : name;
  return { isStandardVariant, variantName };
}

function getBaseProductTypeForCartLine(productType: IProductTypeOverview | IProductTypeListMode) {
  const {
    id, minQuantity, maxQuantity, increment,
    stockSettings, structureType
  } = productType;

  return { id, minQuantity, maxQuantity, increment, stockSettings, structureType };
}

function createBaseCartLine(
  cartLine: ICakeCartLine,
  productType: { discount?: IProductTypeDiscount, name?: string },
) {
  return {
    ...cartLine,
    clientId: cryptoHelper.cartClientId(),
    ...(canHaveOrderLineBuilderId(cartLine, productType as any) && {
      orderLineBuilderId: cartLine.orderLineBuilderId || cryptoHelper.cartClientId(),
    }),
    name: productType.name,
    discount: productType.discount,
  } as Partial<ICakeCartLine>;
}

export function makeCartLineForStoreFromBuilder(cartLine: ICakeCartLine,
                                                productType: IProductType | IUpsellProductType): ICakeCartLine {

  const { details, price: sizePrice } = cartLine.size;
  const isPieces = productType.structureType === PRODUCT_TYPE_STRUCTURE_TYPES.PIECES;
  const productPrice = isPieces ? sizePrice : _getProductPrice(productType, cartLine.variant.id);
  const discountValue = getProductTypeDiscountValue(productType);
  const discountAmount = calcDiscountAmount(productPrice, discountValue);
  const price = calcDiscountedPrice(productPrice, discountValue);

  /**For IProductType | IUpsellProductType compatibility*/
  const extendByCategoryPicture = (productType) => ({
    categoryPictureId: productType.categoryPictureId || null,
    categoryPicture: productType.categoryPicture || null,
  });

  const { id, picture, image, stockSettings } = productType;

  return {
    ...createBaseCartLine(cartLine, productType),
    discountAmount,
    price,
    product: {
      type: {
        id, picture, image, stockSettings,
        ...extendByCategoryPicture(productType)
      },
      variant: cartLine.variant,
      size: {
        id: details.id,
        definition: details.definition,
        measurement: details.measurement,
        shape: details.shape,
        value: details.value,
        pieces: false
      },
    },
    orderLines: _transformForDisplayingInCart(cartLine, productType),
  } as ICakeCartLine;
}

// FOR ADDING PRODUCTS IN GALLERY MODE
export function makeCartLineForStoreFromOverview(cartLine: ICakeCartLine, productType: IProductTypeOverview): ICakeCartLine {
  const discountValue = getProductTypeDiscountValue(productType);
  const price = calcDiscountedPrice(productType.minPrice, discountValue);
  const { isStandardVariant, variantName } = getStandardVariantValues(productType.variantName);

  const { id, image, stockSettings, pieces } = productType;

  return {
    ...createBaseCartLine(cartLine, productType),
    type: CART_LINE_TYPES.PRODUCT,
    price,
    productType: getBaseProductTypeForCartLine(productType),
    product: {
      type: { id, image, stockSettings },
      variant: {
        isStandard: isStandardVariant,
        name: productType.variantName
      },
      size: { pieces },
    },
    size: {
      details: { pieces },
    },
    variant: {
      isStandard: isStandardVariant,
      name: variantName
    },
  } as ICakeCartLine;
}

// FOR ADDING ONE VARIANT NO ADDONS PRODUCTS
export function oneVariantCartLineForStore(cartLine: ICakeCartLine, productType: IProductTypeListMode) {
  const [size] = productType.sizes;
  const [product] = size.products;

  const { id, image, stockSettings } = productType;

  const discountValue = getProductTypeDiscountValue(productType);
  const minPrice = productType.minPrice || product.price || size.price;
  const price = calcDiscountedPrice(minPrice, discountValue);

  const productVariantName = product.variant.name;
  const { isStandardVariant, variantName } = getStandardVariantValues(productVariantName);

  const {
    details: { increment, minimumQuantity, ...sizeDetails },
  } = size;

  return {
    ...createBaseCartLine(cartLine, productType),
    type: CART_LINE_TYPES.PRODUCT,
    price,
    size: {
      details: sizeDetails,
    },
    productType: getBaseProductTypeForCartLine(productType),
    product: {
      type: { id, image, stockSettings },
      variant: {
        isStandard: isStandardVariant,
        name: productVariantName,
      },
      size: {
        id: sizeDetails.id,
        definition: sizeDetails.definition,
        measurement: sizeDetails.measurement,
        shape: sizeDetails.shape,
        value: sizeDetails.value,
      },
    },
    variant: {
      isStandard: isStandardVariant,
      name: variantName
    },
  } as ICakeCartLine;
}

// FOR ADDING MULTIPLE VARIANT NO ADDONS PRODUCTS
export function piecesGroupCartLineForStore(cartLine: ICakeCartLine, productType: IProductTypeListMode) {
  const [size] = productType.sizes;
  const { details } = size;

  const {
    id, image, picture,
    stockSettings, minQuantityPerProduct
  } = productType;

  const productPrice = _getProductPrice(productType, cartLine.variant.id);
  const discountValue = getProductTypeDiscountValue(productType);
  const discountAmount = calcDiscountAmount(productPrice, discountValue);
  const price = calcDiscountedPrice(productPrice, discountValue);

  return {
    ...createBaseCartLine(cartLine, productType),
    discountAmount,
    price,
    size,
    product: {
      type: { id, picture, image, stockSettings },
      variant: cartLine.variant,
      size: {
        id: details.id,
        definition: details.definition,
        measurement: details.measurement,
        shape: details.shape,
        value: details.value,
      },
    },
    productType: {
      ...getBaseProductTypeForCartLine(productType),
      minQuantityPerProduct,
    },
  } as ICakeCartLine;
}

function _getProductPrice(productType: IProductType | IUpsellProductType | IProductTypeListMode, variantId: number): number {
  const allProducts = productType.sizes.reduce((acc, size) => [...acc, ...size.products], []);
  const product = allProducts.find(p => p.variant.id === variantId);
  return product && product.price || 0;
}

function _transformForDisplayingInCart(line: ICakeCartLine,
                                       productType: IProductType | IUpsellProductType) {
  if (!line.productId) {
    return null;
  }

  const _getSizeExtraGroups = () => {
    return line.size.extraGroups
      ? line.size.extraGroups
      : productType.sizes.find(s => s.details.id === line.size.details.id).extraGroups;
  };

  const sizeExtraGroups = _getSizeExtraGroups().map(g => g);

  sizeExtraGroups.forEach(extraGroup => {
    if (line.extraProductsMap[extraGroup.id]) {
      line.extraProductsMap[extraGroup.id].forEach(product => {
        product.extraGroupName = extraGroup.name;
        product.name = product.name || extraGroup.orderLineName;
      });
    }
  });

  const orderLines = values(line.extraProductsMap)
    .reduce((res, arr) => res.concat(arr), [])
    .filter((product) => product.id !== 0)
    .map(({ id, name, price, extraGroupName }) => {
      if (id !== 0) {
        return {
          type: CART_LINE_TYPES.EXTRA_PRODUCT,
          name,
          price,
          discountAmount: 0,
          product: {
            extraGroup: { name: extraGroupName }
          }
        };
      }
    });

  const questionOrderLines = values(line.questionsMap)
    .filter((question, groupId) => Boolean(question.answer))
    .map(({ answer, price, orderLineName, questionText }, key) => {
      return {
        type: CART_LINE_TYPES.QUESTION,
        details: { answer },
        price,
        discountAmount: 0,
        name: orderLineName || questionText
      };
    });

  orderLines.push(...questionOrderLines);

  if (line.imagesForPrinting && line.imagesForPrinting.length) {
    line.imagesForPrinting.forEach(({ orderLineName, price, url }) => {
      orderLines.push({
        type: CART_LINE_TYPES.PICTURE,
        name: orderLineName,
        price,
        discountAmount: 0,
        details: { url }
      });
    });
  }

  if (line.greetingCard && line.greetingCard.checked && line.greetingCard.text) {
    orderLines.push({
      type: CART_LINE_TYPES.GREETING_CARD,
      price: line.greetingCard.price,
      discountAmount: 0,
      details: { text: line.greetingCard.text }
    });
  }

  return orderLines.filter((l) => Boolean(l));
}
